import { FAVORITE_IMAGE_VARIANTS, FavoriteImageSet } from '@pcp/core/interfaces';
import { deriveImageVariants, DeriveImageVariantsDeps } from 'shared/fabric-drawing/tools';
import { SlideCanvasExport } from 'shared/classroom/preview-screen/slide-rendering/ui';

/**
 * Assemble the favorite image set from one canvas export (`canvas-slide-favorites` D2): the
 * master IS the `high` variant; `med`/`low` are full-master downscales to the contract heights.
 * A uniform ladder (panorama) has no separate `med`/`low` — the master is the whole ladder at one
 * native resolution, so the set simply omits them and the master travels once; the service stores
 * those bytes under all three display-variant names (`panorama-single-part-upload` D1/D5). The
 * thumbs always render the save-time viewport at the exact contract sizes, under either shape.
 */
export async function buildFavoriteImageSet(
    exported: SlideCanvasExport,
    deps?: DeriveImageVariantsDeps,
): Promise<FavoriteImageSet> {
    const thumbs = {
        thumb_n: FAVORITE_IMAGE_VARIANTS.thumb_n,
        thumb_h: FAVORITE_IMAGE_VARIANTS.thumb_h,
    };

    if (exported.uniformLadder) {
        const derived = await deriveImageVariants(
            { master: exported.master, thumbSourceRect: exported.viewport, ladder: {}, thumbs },
            deps,
        );
        return {
            high: exported.master,
            thumb_n: derived.thumb_n,
            thumb_h: derived.thumb_h,
        };
    }

    const derived = await deriveImageVariants(
        {
            master: exported.master,
            thumbSourceRect: exported.viewport,
            ladder: {
                med: { height: FAVORITE_IMAGE_VARIANTS.med.height },
                low: { height: FAVORITE_IMAGE_VARIANTS.low.height },
            },
            thumbs,
        },
        deps,
    );
    return {
        high: exported.master,
        med: derived.med,
        low: derived.low,
        thumb_n: derived.thumb_n,
        thumb_h: derived.thumb_h,
    };
}
